// frontend/src/components/common/ErrorBoundary.jsx
// Captura errores de render que, sin esto, dejaban toda la aplicación en pantalla blanca.
// React desmonta el árbol completo ante una excepción no capturada en render/efectos: un
// fallo puntual de una pantalla (o una respuesta inesperada del servidor) tumbaba la app
// entera. Acá se contiene el error, se muestra un aviso legible y se ofrece reintentar sin
// perder la sesión.
import React from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Card = styled.div`
  max-width: 640px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text};
`;

const Titulo = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: 1.15rem;
  color: ${({ theme }) => theme.colors.dangerTxt};
`;

const Mensaje = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const Detalle = styled.pre`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.dangerBg};
  color: ${({ theme }) => theme.colors.dangerTxt};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
  overflow: auto;
`;

const Acciones = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const Boton = styled.button`
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background-color: ${({ theme, $primario }) => ($primario ? theme.colors.primary : theme.colors.surface)};
  color: ${({ theme, $primario }) => ($primario ? theme.colors.onPrimary : theme.colors.text)};
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: ${({ theme, $primario }) => ($primario ? theme.colors.primaryHover : theme.colors.surfaceHover)};
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Dejar rastro en consola para diagnóstico; el usuario ve el aviso de abajo.
    console.error('[ErrorBoundary] Error no capturado en render:', error, info?.componentStack);
  }

  handleReintentar = () => {
    // Limpiar el error re-monta los hijos: alcanza para fallos transitorios (una respuesta
    // inesperada, un dato que llegó incompleto) sin obligar a recargar toda la aplicación.
    this.setState({ error: null });
  };

  handleRecargar = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Wrapper>
        <Card>
          <Titulo>Se produjo un error inesperado en esta pantalla</Titulo>
          <Mensaje>
            El resto del sistema sigue funcionando. Podés reintentar; si el problema se repite,
            recargá la página o avisá al administrador.
          </Mensaje>
          <Detalle>{String(error?.message || error)}</Detalle>
          <Acciones>
            <Boton type="button" $primario onClick={this.handleReintentar}>Reintentar</Boton>
            <Boton type="button" onClick={this.handleRecargar}>Recargar la página</Boton>
          </Acciones>
        </Card>
      </Wrapper>
    );
  }
}

export default ErrorBoundary;
